import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Save, Calculator, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PricingBadge } from '@/components/opportunities/OppBadges';
import { fmtAmount, QUANTITY_SOURCES, RATE_SOURCE_TYPES, RATE_BASIS } from '@/lib/format';
import api from '@/lib/api';
import { toast } from 'sonner';

const tmp = () => `tmp-${Math.random().toString(36).slice(2)}`;
const newLine = () => ({ id: tmp(), description: '', quantity: '', unit: 'LS', quantity_source: 'User entry', rate: '', rate_basis: 'base', rate_source_type: 'manual_approved', rate_source_doc: '', rate_source_page: '', rate_confirmed: false, notes: '' });
const newMarkup = () => ({ type: 'overhead', mode: 'pct', value: '', applies_to: 'subtotal', source: '', confirmed: false });

/**
 * Shared deterministic Calculation Builder — the single source of truth for the
 * opportunity, notice and change order. All arithmetic happens on the backend
 * calculator; this component only captures user-confirmed quantities and rates.
 */
export const CalculationBuilder = ({ opportunityId, calculation, currency = 'USD', onSaved }) => {
  const [lines, setLines] = useState([]);
  const [tender, setTender] = useState({ type: 'none', percentage: '', applies_to: 'Entire BOQ', source_document: '', source_page: '', confirmed: false });
  const [markups, setMarkups] = useState([]);
  const [cur, setCur] = useState(currency);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const c = calculation || {};
    const allowed = new Set(RATE_SOURCE_TYPES.map((r) => r.value));
    setLines((c.lines || []).map((l) => ({
      ...newLine(), ...l,
      quantity: l.quantity ?? '', rate: l.rate ?? '',
      // Rate Card is retired as a source; fall back to manual for any legacy rows.
      rate_source_type: allowed.has(l.rate_source_type) ? l.rate_source_type : 'manual_approved',
    })));
    setTender({ ...{ type: 'none', percentage: '', applies_to: 'Entire BOQ', source_document: '', source_page: '', confirmed: false }, ...(c.tender_adjustment || {}) });
    setMarkups((c.markups || []).map((m) => ({ ...newMarkup(), ...m, value: m.value ?? '' })));
    setCur(c.currency || currency);
  }, [calculation, currency]);

  const bd = (calculation || {}).breakdown || {};
  const status = (calculation || {}).pricing_status || 'unquantified';
  const missing = (calculation || {}).missing || [];

  const upLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        currency: cur,
        lines: lines.filter((l) => (l.description || '').trim()).map((l) => ({
          id: String(l.id || '').startsWith('tmp-') ? undefined : l.id,
          description: l.description, quantity: l.quantity === '' ? null : Number(l.quantity),
          unit: l.unit || 'LS', quantity_source: l.quantity_source || 'User entry',
          rate: l.rate === '' ? null : Number(l.rate), rate_basis: l.rate_basis || 'base',
          rate_source_type: l.rate_source_type || 'manual_approved', rate_source_doc: l.rate_source_doc || '',
          rate_source_page: l.rate_source_page || '', rate_confirmed: !!l.rate_confirmed, notes: l.notes || '',
        })),
        tender_adjustment: { ...tender, percentage: Number(tender.percentage || 0) },
        markups: markups.filter((m) => m.value !== '' ).map((m) => ({ ...m, value: Number(m.value || 0) })),
      };
      const r = await api.put(`/opportunities/${opportunityId}/calculation`, payload);
      toast.success('Calculation saved & recomputed');
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save calculation');
    } finally {
      setSaving(false);
    }
  };

  const tenderLabel = tender.type === 'discount' ? 'Discount' : tender.type === 'premium' ? 'Premium' : 'None';

  return (
    <Card className="p-0 overflow-hidden" data-testid="calc-builder">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Calculator className="h-4 w-4 text-brand" />Deterministic calculation</div>
        <div className="flex items-center gap-2">
          <PricingBadge status={status} testid="calc-pricing-status" />
          <Button size="sm" className="h-8 bg-money hover:bg-money-700" onClick={save} disabled={saving} data-testid="calc-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}Save & recalculate
          </Button>
        </div>
      </div>

      {/* Line items */}
      <div className="p-4 space-y-3">
        <div className="rounded-lg bg-brand-50/60 border border-brand-100 px-3 py-2 flex items-start gap-2" data-testid="calc-manual-hint">
          <Info className="h-3.5 w-3.5 text-brand mt-0.5 shrink-0" />
          <p className="text-[11px] text-slate-600">
            Enter each work component with a <b>confirmed quantity and rate</b> — this is the pricing source of truth for the notice and change order.
            Tick <b>Rate verified</b> once you&rsquo;ve checked the figure against your BOQ / quotation. Use <b>Effective</b> basis for rates that already include the tender adjustment so it isn&rsquo;t applied twice.
          </p>
        </div>
        {lines.length === 0 && <p className="text-xs text-slate-500 text-center py-3">No line items yet. Add work components with confirmed quantities and rates.</p>}
        {lines.map((l, i) => (
          <div key={l.id} className="rounded-lg border border-slate-200 p-3" data-testid={`calc-line-${i}`}>
            <div className="flex items-start gap-2">
              <Input value={l.description} onChange={(e) => upLine(i, 'description', e.target.value)} placeholder="Work item / description" className="h-8 flex-1" data-testid={`calc-desc-${i}`} />
              <button onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="text-slate-300 hover:text-danger-600 mt-1.5" data-testid={`calc-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Quantity</label>
                <Input type="number" step="any" value={l.quantity} onChange={(e) => upLine(i, 'quantity', e.target.value)} className="h-8 font-mono" placeholder="—" data-testid={`calc-qty-${i}`} />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Unit</label>
                <Input value={l.unit} onChange={(e) => upLine(i, 'unit', e.target.value)} className="h-8" placeholder="cum" data-testid={`calc-unit-${i}`} />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Quantity source</label>
                <Select value={l.quantity_source} onValueChange={(v) => upLine(i, 'quantity_source', v)}>
                  <SelectTrigger className="h-8" data-testid={`calc-qtysrc-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{QUANTITY_SOURCES.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Rate ({cur})</label>
                <Input type="number" step="any" value={l.rate} onChange={(e) => upLine(i, 'rate', e.target.value)} className="h-8 font-mono" placeholder="—" data-testid={`calc-rate-${i}`} />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Rate basis</label>
                <Select value={l.rate_basis} onValueChange={(v) => upLine(i, 'rate_basis', v)}>
                  <SelectTrigger className="h-8" data-testid={`calc-ratebasis-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{RATE_BASIS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Rate source</label>
                <Select value={l.rate_source_type} onValueChange={(v) => upLine(i, 'rate_source_type', v)}>
                  <SelectTrigger className="h-8" data-testid={`calc-ratesrc-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{RATE_SOURCE_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-semibold">Source doc / page</label>
                <div className="flex gap-1">
                  <Input value={l.rate_source_doc} onChange={(e) => upLine(i, 'rate_source_doc', e.target.value)} className="h-8" placeholder="BOQ" data-testid={`calc-ratedoc-${i}`} />
                  <Input value={l.rate_source_page} onChange={(e) => upLine(i, 'rate_source_page', e.target.value)} className="h-8 w-14" placeholder="p." data-testid={`calc-ratepage-${i}`} />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-600 h-8" data-testid={`calc-verified-wrap-${i}`}>
                  <Checkbox checked={!!l.rate_confirmed} onCheckedChange={(v) => upLine(i, 'rate_confirmed', !!v)} data-testid={`calc-verified-${i}`} />Rate verified
                </label>
              </div>
            </div>
            <div className="mt-2 text-xs font-mono text-slate-500 flex items-center justify-between">
              <span data-testid={`calc-line-formula-${i}`}>{l.quantity !== '' && l.rate !== '' ? `${Number(l.quantity)} ${l.unit} × ${fmtAmount(l.rate, cur)} = ${fmtAmount(Number(l.quantity) * Number(l.rate), cur)}` : 'Unpriced — quantity and rate required'}</span>
              {l.rate_basis === 'effective' && <span className="text-amber-600 not-italic">Effective rate — tender NOT re-applied</span>}
            </div>
          </div>
        ))}
        <Button size="sm" variant="outline" className="h-8" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="calc-add-line"><Plus className="h-3.5 w-3.5 mr-1.5" />Add line item</Button>
      </div>

      {/* Tender adjustment */}
      <div className="px-4 pb-3">
        <div className="rounded-lg border border-slate-200 p-3" data-testid="calc-tender">
          <div className="text-xs font-semibold text-slate-700 mb-2">Tender discount / premium <span className="font-normal text-slate-400">— applied ONLY to base-rate lines</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select value={tender.type} onValueChange={(v) => setTender((t) => ({ ...t, type: v }))}>
              <SelectTrigger className="h-8" data-testid="calc-tender-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="discount">Discount</SelectItem><SelectItem value="premium">Premium</SelectItem></SelectContent>
            </Select>
            <Input type="number" step="any" value={tender.percentage} onChange={(e) => setTender((t) => ({ ...t, percentage: e.target.value }))} className="h-8 font-mono" placeholder="%" disabled={tender.type === 'none'} data-testid="calc-tender-pct" />
            <Input value={tender.source_document} onChange={(e) => setTender((t) => ({ ...t, source_document: e.target.value }))} className="h-8" placeholder="Source doc" disabled={tender.type === 'none'} data-testid="calc-tender-doc" />
            <label className="flex items-center gap-1.5 text-xs text-slate-600 h-8">
              <Checkbox checked={!!tender.confirmed} onCheckedChange={(v) => setTender((t) => ({ ...t, confirmed: !!v }))} disabled={tender.type === 'none'} data-testid="calc-tender-confirm" />Confirmed
            </label>
          </div>
        </div>
      </div>

      {/* Markups */}
      <div className="px-4 pb-3">
        <div className="rounded-lg border border-slate-200 p-3" data-testid="calc-markups">
          <div className="text-xs font-semibold text-slate-700 mb-2">Markups <span className="font-normal text-slate-400">— applied to subtotal after tender</span></div>
          {markups.map((m, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2" data-testid={`calc-markup-${i}`}>
              <Select value={m.type} onValueChange={(v) => setMarkups((ms) => ms.map((x, j) => j === i ? { ...x, type: v } : x))}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="overhead">Overhead</SelectItem><SelectItem value="profit">Profit</SelectItem><SelectItem value="tax">Tax</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
              <Select value={m.mode} onValueChange={(v) => setMarkups((ms) => ms.map((x, j) => j === i ? { ...x, mode: v } : x))}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pct">%</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
              </Select>
              <Input type="number" step="any" value={m.value} onChange={(e) => setMarkups((ms) => ms.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} className="h-8 font-mono" placeholder="value" data-testid={`calc-markup-val-${i}`} />
              <Input value={m.source} onChange={(e) => setMarkups((ms) => ms.map((x, j) => j === i ? { ...x, source: e.target.value } : x))} className="h-8" placeholder="Source clause" />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5 text-xs text-slate-600"><Checkbox checked={!!m.confirmed} onCheckedChange={(v) => setMarkups((ms) => ms.map((x, j) => j === i ? { ...x, confirmed: !!v } : x))} data-testid={`calc-markup-confirm-${i}`} />Confirmed</label>
                <button onClick={() => setMarkups((ms) => ms.filter((_, j) => j !== i))} className="text-slate-300 hover:text-danger-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMarkups((ms) => [...ms, newMarkup()])} data-testid="calc-add-markup"><Plus className="h-3 w-3 mr-1" />Add markup</Button>
        </div>
      </div>

      {/* Breakdown */}
      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 text-sm" data-testid="calc-breakdown">
        <div className="flex justify-between py-0.5"><span className="text-slate-500">Base subtotal</span><span className="font-mono">{fmtAmount(bd.base_subtotal, cur)}</span></div>
        {(calculation?.tender_adjustment?.applied) && (
          <div className="flex justify-between py-0.5"><span className="text-slate-500">Tender {tenderLabel.toLowerCase()} ({tender.percentage || 0}% on base-rate lines)</span><span className="font-mono">{fmtAmount(bd.tender_amount, cur)}</span></div>
        )}
        <div className="flex justify-between py-0.5"><span className="text-slate-500">Subtotal after tender</span><span className="font-mono">{fmtAmount(bd.after_tender_subtotal, cur)}</span></div>
        <div className="flex justify-between py-0.5"><span className="text-slate-500">Markups</span><span className="font-mono">{fmtAmount(bd.markup_total, cur)}</span></div>
        <div className="flex justify-between pt-2 mt-1 border-t border-slate-200 font-semibold">
          <span>Final amount</span>
          {status === 'fully_priced'
            ? <span className="font-mono text-money-700" data-testid="calc-final-amount">{fmtAmount((calculation || {}).final_amount, cur)}</span>
            : <span className="text-amber-600 text-xs" data-testid="calc-final-amount">{status === 'partially_priced' ? 'Partial — not final' : 'Unquantified'}</span>}
        </div>
        {missing.length > 0 && (
          <div className="mt-2 text-xs text-amber-700 flex items-start gap-1.5" data-testid="calc-missing">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Missing to price fully: {missing.join('; ')}</span>
          </div>
        )}
      </div>
    </Card>
  );
};
